import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  BarChart3, TrendingUp, DollarSign, Eye, Star, Calendar, CheckCircle,
  Target, Heart
} from 'lucide-react';
import { toast } from 'sonner';

export default function ArtistAnalytics() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/artist/analytics');
      setAnalytics(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Artist profile not found');
        navigate('/become-artist');
      } else {
        toast.error('Failed to load analytics');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (loading) {
    return (
      <div className="relative z-10 min-h-screen pt-24 pb-12 px-4 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500" />
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="relative z-10 min-h-screen pt-24 pb-12 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <BarChart3 className="w-16 h-16 mx-auto mb-4 text-white/40" />
          <h2 className="text-2xl font-bold text-white mb-2">No Analytics Data</h2>
          <p className="text-white/60">Analytics will appear once you start getting bookings.</p>
        </div>
      </div>
    );
  }

  // Calculate growth percentages (mock for now since we don't track historical)
  const conversionRate = analytics.total_bookings > 0 
    ? Math.round(((analytics.paid_bookings + analytics.completed_bookings) / analytics.total_bookings) * 100) 
    : 0;

  const avgBookingValue = analytics.paid_bookings > 0 
    ? Math.round(analytics.total_earnings / analytics.paid_bookings)
    : 0;

  // Sort monthly stats by date
  const sortedMonths = Object.entries(analytics.monthly_stats || {})
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-6);

  const maxBookings = Math.max(...sortedMonths.map(([, data]) => data.bookings), 1);
  const maxRevenue = Math.max(...sortedMonths.map(([, data]) => data.revenue), 1);

  // Format month name
  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'short' });
  };

  return (
    <div className="relative z-10 min-h-screen pt-24 pb-12 px-4" data-testid="artist-analytics-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <BarChart3 className="w-8 h-8 text-violet-400" />
          <h1 className="text-3xl font-bold text-white">Analytics Dashboard</h1>
        </div>
        <p className="text-white/60 mb-8">Track your performance and grow your artist career</p>

        {/* Stats Grid - Top Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {/* Total Earnings */}
          <Card className="bg-gradient-to-br from-green-500/20 to-emerald-500/10 border-green-500/30">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-green-300/80 text-sm font-medium mb-1">Total Earnings</p>
                  <p className="text-3xl font-bold text-white">${analytics.total_earnings?.toLocaleString() || '0'}</p>
                  <p className="text-sm text-white/40 mt-1">From paid bookings</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Total Bookings */}
          <Card className="bg-gradient-to-br from-violet-500/20 to-purple-500/10 border-violet-500/30">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-violet-300/80 text-sm font-medium mb-1">Total Bookings</p>
                  <p className="text-3xl font-bold text-white">{analytics.total_bookings || 0}</p>
                  <p className="text-sm text-white/40 mt-1">{analytics.completed_bookings || 0} completed</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-violet-500/20 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-violet-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Profile Views */}
          <Card className="bg-gradient-to-br from-blue-500/20 to-cyan-500/10 border-blue-500/30">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-blue-300/80 text-sm font-medium mb-1">Profile Views</p>
                  <p className="text-3xl font-bold text-white">{analytics.profile_views?.toLocaleString() || 0}</p>
                  <p className="text-sm text-white/40 mt-1">All time</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                  <Eye className="w-6 h-6 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Average Rating */}
          <Card className="bg-gradient-to-br from-yellow-500/20 to-amber-500/10 border-yellow-500/30">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-yellow-300/80 text-sm font-medium mb-1">Average Rating</p>
                  <p className="text-3xl font-bold text-white">{analytics.avg_rating || 'N/A'}</p>
                  <p className="text-sm text-white/40 mt-1">{analytics.total_reviews || 0} reviews</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-yellow-500/20 flex items-center justify-center">
                  <Star className="w-6 h-6 text-yellow-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Bookings Chart */}
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-violet-400" />
                Bookings Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              {sortedMonths.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 mx-auto mb-4 text-white/20" />
                  <p className="text-white/40">No booking data yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Bar Chart */}
                  <div className="flex items-end justify-between gap-2 h-40 px-2">
                    {sortedMonths.map(([month, data]) => {
                      const height = (data.bookings / maxBookings) * 100;
                      return (
                        <div key={month} className="flex-1 flex flex-col items-center group">
                          <div className="relative w-full flex justify-center">
                            <div 
                              className="w-8 bg-gradient-to-t from-violet-600 to-violet-400 rounded-t-lg transition-all duration-300 group-hover:from-violet-500 group-hover:to-violet-300 cursor-pointer"
                              style={{ 
                                height: `${Math.max(height, data.bookings > 0 ? 8 : 0)}px`,
                                minHeight: data.bookings > 0 ? '8px' : '0'
                              }}
                            />
                            {/* Tooltip */}
                            <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-900 px-2 py-1 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                              {data.bookings} bookings
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* X-axis labels */}
                  <div className="flex justify-between gap-2 px-2 border-t border-white/10 pt-3">
                    {sortedMonths.map(([month]) => (
                      <div key={month} className="flex-1 text-center">
                        <span className="text-xs text-white/40">{formatMonth(month)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Revenue Chart */}
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-400" />
                Revenue Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              {sortedMonths.length === 0 ? (
                <div className="text-center py-8">
                  <DollarSign className="w-12 h-12 mx-auto mb-4 text-white/20" />
                  <p className="text-white/40">No revenue data yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Bar Chart */}
                  <div className="flex items-end justify-between gap-2 h-40 px-2">
                    {sortedMonths.map(([month, data]) => {
                      const height = (data.revenue / maxRevenue) * 100;
                      return (
                        <div key={month} className="flex-1 flex flex-col items-center group">
                          <div className="relative w-full flex justify-center">
                            <div 
                              className="w-8 bg-gradient-to-t from-green-600 to-green-400 rounded-t-lg transition-all duration-300 group-hover:from-green-500 group-hover:to-green-300 cursor-pointer"
                              style={{ 
                                height: `${Math.max(height, data.revenue > 0 ? 8 : 0)}px`,
                                minHeight: data.revenue > 0 ? '8px' : '0'
                              }}
                            />
                            {/* Tooltip */}
                            <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-900 px-2 py-1 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                              ${data.revenue.toLocaleString()}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* X-axis labels */}
                  <div className="flex justify-between gap-2 px-2 border-t border-white/10 pt-3">
                    {sortedMonths.map(([month]) => (
                      <div key={month} className="flex-1 text-center">
                        <span className="text-xs text-white/40">{formatMonth(month)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Bottom Stats Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Booking Status Breakdown */}
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Target className="w-5 h-5 text-violet-400" />
                Booking Funnel
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-white/60 flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                    Pending
                  </span>
                  <span className="text-yellow-400 font-semibold">{analytics.pending_bookings || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-white/60 flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-green-500"></span>
                    Paid
                  </span>
                  <span className="text-green-400 font-semibold">{analytics.paid_bookings || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-white/60 flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-violet-500"></span>
                    Completed
                  </span>
                  <span className="text-violet-400 font-semibold">{analytics.completed_bookings || 0}</span>
                </div>
                
                {/* Progress bar */}
                <div className="pt-2">
                  <div className="h-3 bg-white/10 rounded-full overflow-hidden flex">
                    {analytics.total_bookings > 0 && (
                      <>
                        <div 
                          className="h-full bg-violet-500"
                          style={{ width: `${(analytics.completed_bookings / analytics.total_bookings) * 100}%` }}
                        />
                        <div 
                          className="h-full bg-green-500"
                          style={{ width: `${(analytics.paid_bookings / analytics.total_bookings) * 100}%` }}
                        />
                        <div 
                          className="h-full bg-yellow-500"
                          style={{ width: `${(analytics.pending_bookings / analytics.total_bookings) * 100}%` }}
                        />
                      </>
                    )}
                  </div>
                  <p className="text-sm text-white/40 text-center mt-2">
                    {conversionRate}% conversion rate
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Key Metrics */}
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                Key Metrics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 bg-white/5 rounded-xl">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-white/60 text-sm">Avg. Booking Value</p>
                    <DollarSign className="w-4 h-4 text-green-400" />
                  </div>
                  <p className="text-2xl font-bold text-white">${avgBookingValue.toLocaleString()}</p>
                </div>
                <div className="p-4 bg-white/5 rounded-xl">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-white/60 text-sm">Views to Booking</p>
                    <Eye className="w-4 h-4 text-blue-400" />
                  </div>
                  <p className="text-2xl font-bold text-white">
                    {analytics.total_bookings > 0 
                      ? Math.round(analytics.profile_views / analytics.total_bookings) 
                      : analytics.profile_views || 0}:1
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Engagement */}
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Heart className="w-5 h-5 text-pink-400" />
                Engagement
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                  <div>
                    <p className="text-white/60 text-sm">Total Reviews</p>
                    <p className="text-2xl font-bold text-white">{analytics.total_reviews || 0}</p>
                  </div>
                  <Star className="w-8 h-8 text-yellow-400" />
                </div>
                <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                  <div>
                    <p className="text-white/60 text-sm">Profile Saves</p>
                    <p className="text-2xl font-bold text-white">{analytics.favorite_count || 0}</p>
                  </div>
                  <Heart className="w-8 h-8 text-pink-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pro Tips */}
        <Card className="bg-gradient-to-r from-violet-500/10 to-purple-500/10 border-violet-500/30 mt-8">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-5 h-5 text-violet-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Growth Tips</h3>
                <ul className="space-y-2 text-white/60 text-sm">
                  <li>• Add more photos and videos to your gallery to increase profile views</li>
                  <li>• Respond to inquiries quickly - artists who respond within 2 hours get 3x more bookings</li>
                  <li>• Keep your calendar updated to appear in availability searches</li>
                  <li>• Ask satisfied clients to leave reviews - profiles with 5+ reviews convert 40% better</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
